import React, { useEffect, useState } from "react";
import Select from "react-select";
import { Form } from "react-bootstrap";

import ErrorMessageComponent from "../ErrorMessage/ErrorMessage";
import { handleApiResponse } from "../../utils/handleApiResponse";
import "./form-select.scss";

export interface Option {
  value: string;
  label: string;
}

interface FormSelectProps {
  label: string;
  onChange: (value: string | null, label: string) => void;
  placeholder?: string;
  required?: boolean;
  name: string;
  className?: string;
  labelClassName?: string;
  touched?: boolean;
  error?: string;
  defaultValue?: Option;
  isInvalid?: string | boolean;
  isValid?: boolean;
  setFieldError?: (field: string, message: string) => void;
  setFieldTouched?: (field: string, touched?: boolean) => void;
  isSearchable?: boolean;
  isClearable?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
  isMulti?: boolean;
  fetchQuery?: any;
  fetchQueryParams?: any;
  optionLabelName?: string;
  shouldFetch?: boolean;
  persistedOption?: Option | null;
  value?: string | null;
}

const AsyncFormSelect: React.FC<FormSelectProps> = ({
  onChange,
  name,
  touched,
  error,
  setFieldError,
  setFieldTouched,
  placeholder,
  label,
  required,
  className,
  labelClassName = "",
  defaultValue,
  isSearchable = false,
  isLoading = false,
  isDisabled = false,
  isClearable = false,
  isMulti = false,
  fetchQuery,
  fetchQueryParams,
  optionLabelName,
  shouldFetch,
  persistedOption,
  value,
}) => {
  const [selectedOption, setSelectedOption] = useState<Option | null>(
    persistedOption ?? null
  );

  const [apiResponse, setApiResponse] = useState<any>();
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const handleBlur = () => {
    if (setFieldTouched) {
      setFieldTouched(name, true);
    }
  };

  const handleChange = (option: Option | null) => {
    if (setFieldError) {
      setFieldError(name, "");
    }
    onChange(option?.value ?? "", option?.label ?? "");
  };

  const fetchOptions = async (args?: Record<string, any>) => {
    try {
      const response = await fetchQuery({
        ...fetchQueryParams,
        ...args,
      }).unwrap();

      if (!response?.data || !Array.isArray(response.data)) {
        console.error("Invalid response structure:", response);
        return;
      }

      setApiResponse((prev: any) => {
        const newData = [...(prev?.data || []), ...response?.data];

        const uniqueData = newData.filter(
          (item, index, self) =>
            index === self.findIndex((t) => t.id === item.id)
        );

        return {
          ...response,
          data: uniqueData,
        };
      });
    } catch (error: any) {
      console.log(error);

      handleApiResponse({
        response: error,
        type: "error",
        toastId: "Async Form Select - Fetch Brand List Error",
      });
    }
  };

  useEffect(() => {
    if (shouldFetch) {
      fetchOptions();
    }
    // eslint-disable-next-line
  }, []);

  const options = [
    ...(persistedOption ? [persistedOption] : []),
    ...(apiResponse?.data?.map((eachData: any) => ({
      label: eachData?.[optionLabelName!],
      value: eachData?.id,
    })) || []),
  ].filter(
    (item: Option, index: number, self: Array<Option>) =>
      index === self.findIndex((t: any) => t.value === item.value)
  );

  const handleLoadMore = async () => {
    if (!apiResponse?.next_start_key || isFetchingMore) return;

    try {
      setIsFetchingMore(true);
      if (!apiResponse?.next_start_key) return null;
      fetchOptions({ startKey: apiResponse?.next_start_key });
    } catch (error: any) {
      console.log(error);

      handleApiResponse({
        response: error,
        type: "error",
        toastId: "Async Form Select - Load More Brand List Error",
      });
    } finally {
      setIsFetchingMore(false);
    }
  };

  useEffect(() => {
    if (value && apiResponse?.data) {
      const foundOption = options.find((option) => option.value === value);

      if (foundOption) {
        setSelectedOption(foundOption);
      }
    } else {
      setSelectedOption(persistedOption ?? null);
    }
    // eslint-disable-next-line
  }, [value, apiResponse?.data, persistedOption]);

  return (
    <div className="infinite-scroll-select">
      {label && (
        <Form.Label className={`m-0 fw-500 ${labelClassName}`}>
          {label}
          {required && <span>*</span>}
        </Form.Label>
      )}

      <Select
        className={`select-control-modal-style ${className}`}
        classNamePrefix="select"
        defaultValue={defaultValue}
        placeholder={placeholder}
        name={name}
        options={options}
        value={selectedOption}
        onChange={(val) => {
          handleChange(val);
          setSelectedOption(val);
        }}
        onBlur={handleBlur}
        isDisabled={isDisabled}
        isLoading={isFetchingMore}
        isClearable={isClearable}
        isSearchable={isSearchable}
        styles={{
          control: (baseStyles, state) => ({
            ...baseStyles,
            cursor: "pointer",
            borderColor: touched && error ? "#dc3545" : "hsl(0, 0%, 80%)",
          }),
          option: (baseStyles) => ({ ...baseStyles, cursor: "pointer" }),
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        }}
        // isMulti={isMulti}
        // defaultMenuIsOpen
        onMenuScrollToBottom={handleLoadMore}
      />
      {touched && error && (
        <ErrorMessageComponent touched={touched} error={error} />
      )}
    </div>
  );
};

export default AsyncFormSelect;
